// Comunidades (lote 7, item 6): a turma organizada da firma, do bar, da família, do clube. Na aba Ligas, ao lado da
// Liga da Semana: suas comunidades, convites, busca e "Criar comunidade" (cadeado fora do Ouro). Dentro: mural (só
// dono e moderadores publicam; membros reagem), mesas só dos membros, ranking interno, eventos e membros.
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ESCUDOS, rotuloDaDivisao } from '@truco6/gamificacao';
import type { SalaNaLista } from '@truco6/mesa/protocolo';
import { NOME_DO_PLANO, pode } from '@truco6/planos';
import { ofensivo } from '@truco6/moderacao';
import { ErroDaApi } from '../comum/api.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { BotaoPerfil } from '../comum/BotaoPerfil.tsx';
import {
  COMUNIDADES_POR_DONO, DESCRICAO_MAX, type EventoNaTela, type FichaComunidade, type LinhaDoRanking, MEMBROS_MAX, type MembroNaLista, type MinhasComunidades,
  NOME_DA_COMUNIDADE_MAX, NOME_DO_TIPO, POST_MAX, type PedidoDeEntrada, type PeriodoDoRanking, type PostNaTela, REACOES_DO_MURAL, type ResumoComunidade,
  TIPOS_DE_COMUNIDADE, TITULO_EVENTO_MAX, type TipoComunidade, apagarPost, buscarComunidades, cancelarEvento, comunidadePorConvite, convidarAmigos, criarComunidade,
  criarEvento, decidirPedido, definirPapel, denunciarComunidade, denunciarPost, editarComunidade, entrarNaComunidade, instanteDe, lerComunidade, lerEventos, lerImagem,
  lerMembros, lerMesasDaComunidade, lerMural, lerRankingDaComunidade, linkDaComunidade, minhasComunidades, novoConvite, publicarNoMural, quandoDoEvento,
  reagirAoPost, removerMembro, sairDaComunidade, transferirComunidade,
} from '../comum/comunidades.ts';
import { type AmigoNaLista, lerAmigos } from '../comum/amigos.ts';
import { conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { quandoFoi } from '../comum/quando.ts';
import { type AbaDaComunidade, ir, voltar } from '../comum/rotas.ts';
import { irParaMesa } from '../comum/limite.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { CampoCidade } from '../telas/CampoCidade.tsx';
import { CriarSala } from '../telas/CriarSala.tsx';
import { Carregando, Tela, Topo } from '../telas/comum.tsx';
import { CampeonatosDaComunidade } from './campeonatos.tsx';
import { Escudo } from './trios.tsx';

const PAPEL: Record<string, string> = { dono: 'Dono', moderador: 'Moderador', membro: 'Membro' };
const linhaDaComunidade = (c: ResumoComunidade) =>
  [NOME_DO_TIPO[c.tipo], c.cidade, `${c.membros} ${c.membros === 1 ? 'membro' : 'membros'}`].filter(Boolean).join(' · ');

// As abas da aba Ligas: o Ranking (Jogadores, Trios e Rei do Pote), as Comunidades e os Campeonatos. Com a Liga da
// Semana no ar, a primeira aba volta a se chamar Liga da Semana.
export function AbasDasLigas({ atual }: { atual: 'liga' | 'comunidades' | 'campeonatos' }) {
  const abas = [['liga', ligaNoAr() ? 'Liga da Semana' : 'Ranking'], ['comunidades', 'Comunidades'], ['campeonatos', 'Campeonatos']] as const;
  return (
    <div className="segmentos-periodo abas-ligas" role="tablist" aria-label="Ligas">
      {abas.map(([a, nome]) => (
        <button key={a} role="tab" aria-selected={atual === a} className={atual === a ? 'on' : undefined}
          onClick={() => { if (atual !== a) ir(a === 'liga' ? { tela: 'ranking' } : { tela: 'ranking', aba: a }, true); }}>{nome}</button>
      ))}
    </div>
  );
}

function LinhaComunidade({ c, etiqueta, aoAbrir }: { c: ResumoComunidade; etiqueta?: string | null; aoAbrir: () => void }) {
  return (
    <button className="cartao linha-trio linha-comunidade" onClick={aoAbrir}>
      <Escudo id={c.escudo} tamanho={44} />
      <span className="quem"><b>{c.nome}</b><small>{linhaDaComunidade(c)}</small></span>
      {etiqueta && <span className="etiquetas"><span className={'etiqueta' + (etiqueta === 'Dono' ? ' capitao' : '')}>{etiqueta}</span></span>}
    </button>
  );
}

// A aba Comunidades: convites, as suas, a busca e o Criar.
export function ComunidadesTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [m, setM] = useState<MinhasComunidades | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [achadas, setAchadas] = useState<ResumoComunidade[] | null>(null);
  useEffect(() => {
    let vivo = true;
    minhasComunidades(sessao).then((x) => { if (vivo) setM(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  useEffect(() => {
    const t = texto.trim();
    if (t.length < 2) { setAchadas(null); return; }
    let vivo = true;
    const prazo = setTimeout(() => { buscarComunidades(sessao, t).then((r) => { if (vivo) setAchadas(r.comunidades); }, () => {}); }, 300);
    return () => { vivo = false; clearTimeout(prazo); };
  }, [texto]);
  const ouro = pode(perfil.plano, 'criarLiga');
  const minhas = new Set(m?.comunidades.map((c) => c.id));

  return (
    <Tela larga>
      {/* Comunidades é uma aba de Ligas, que é aba principal: sem o voltar. */}
      <Topo titulo="Comunidades" />
      <AbasDasLigas atual="comunidades" />
      <div className="comunidades">
        <p className="sub">A turma da firma, do bar, da família ou do clube: mural, mesas só dos membros, ranking e eventos.</p>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {!m && !erro && <p className="carregando" role="status">Carregando...</p>}
        {/* A partir de 720 px, os convites e as suas comunidades vão para a coluna da direita. */}
        <div className="lista-principal">
        {m && m.convites.length > 0 && (
          <section aria-labelledby="h-convites">
            <h2 id="h-convites" className="rotulo">Convites · {m.convites.length}</h2>
            <ul className="lista-trios">
              {m.convites.map((c) => (
                <li key={c.id}>
                  <LinhaComunidade c={c} etiqueta={null} aoAbrir={() => ir({ tela: 'convite-comunidade', codigo: c.convite })} />
                  <small className="nota convite-de">{c.deApelido} chamou você</small>
                </li>
              ))}
            </ul>
          </section>
        )}
        {m && (
          <section aria-labelledby="h-minhas">
            <h2 id="h-minhas" className="rotulo">Suas comunidades{m.comunidades.length ? ` · ${m.comunidades.length}` : ''}</h2>
            {m.comunidades.length === 0
              ? <p className="cartao vazio-lista">Você ainda não está em nenhuma comunidade. Procure uma pública ou entre pelo link de convite.</p>
              : (
                <ul className="lista-trios">
                  {m.comunidades.map((c) => (
                    <li key={c.id}><LinhaComunidade c={c} etiqueta={c.papel === 'membro' ? null : PAPEL[c.papel]} aoAbrir={() => ir({ tela: 'comunidade', id: c.id })} /></li>
                  ))}
                </ul>
              )}
          </section>
        )}
        </div>
        <section aria-labelledby="h-busca" className="campo">
          <h2 id="h-busca" className="rotulo">Procurar comunidade</h2>
          <input type="search" value={texto} maxLength={40} placeholder="Nome da comunidade" aria-label="Procurar comunidade pelo nome"
            onChange={(e) => setTexto(e.target.value)} />
          {achadas && achadas.length === 0 && <p className="nota">Nenhuma comunidade pública com esse nome.</p>}
          {achadas && achadas.length > 0 && (
            <ul className="lista-trios">
              {achadas.map((c) => (
                <li key={c.id}><LinhaComunidade c={c} etiqueta={minhas.has(c.id) ? 'Você está' : c.aprovacao ? 'Com aprovação' : null} aoAbrir={() => ir({ tela: 'comunidade', id: c.id })} /></li>
              ))}
            </ul>
          )}
        </section>
        {m && (m.podeCriar
          ? <button className="btn1 largo" onClick={() => ir({ tela: 'nova-comunidade' })}>CRIAR COMUNIDADE</button>
          : (
            <div className="cadeado-tentos">
              <p className="cadeado">{ouro ? `O plano Ouro cria até ${COMUNIDADES_POR_DONO} comunidades.` : 'Criar comunidade é do plano Ouro.'}</p>
              {!ouro && <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'mensal' })}>Ver planos</button>}
            </div>
          ))}
      </div>
    </Tela>
  );
}

export function Chave({ rotulo, ligada, travado = false, aoMudar }: { rotulo: string; ligada: boolean; travado?: boolean; aoMudar: (v: boolean) => void }) {
  return (
    <button role="switch" aria-checked={ligada} aria-label={rotulo} disabled={travado} className={'chave' + (ligada ? ' on' : '')}
      onClick={() => aoMudar(!ligada)}><i /></button>
  );
}

export function Opcoes<T extends string>({ rotulo, valor, opcoes, aoMudar, travado = false, classe = '' }: {
  rotulo: string; valor: T; opcoes: readonly (readonly [T, string])[]; aoMudar: (v: T) => void; travado?: boolean; classe?: string;
}) {
  return (
    <div className={'segmentos largo' + (classe ? ' ' + classe : '')} role="radiogroup" aria-label={rotulo}>
      {opcoes.map(([v, nome]) => (
        <button key={v} role="radio" aria-checked={valor === v} disabled={travado} className={valor === v ? 'on' : undefined} onClick={() => aoMudar(v)}>{nome}</button>
      ))}
    </div>
  );
}

function EscolherEscudo({ valor, travado, aoMudar }: { valor: string; travado: boolean; aoMudar: (id: string) => void }) {
  return (
    <div className="escudos" role="radiogroup" aria-label="Escudo">
      {ESCUDOS.map((e) => (
        <button key={e.id} role="radio" aria-checked={valor === e.id} aria-label={'Escudo ' + e.emoji} className={valor === e.id ? 'on' : undefined}
          disabled={travado} onClick={() => aoMudar(e.id)}>
          <Escudo id={e.id} tamanho={34} />
        </button>
      ))}
    </div>
  );
}

const textoOfensivo = (t: string) => t.trim() !== '' && ofensivo(t);

export function CriarComunidadeTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [nome, setNome] = useState('');
  const [escudo, setEscudo] = useState<string>(ESCUDOS[0].id);
  const [tipo, setTipo] = useState<TipoComunidade>('turma');
  const [cidade, setCidade] = useState<number | null>(null);
  const [cidadePendente, setCidadePendente] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [publica, setPublica] = useState(true);
  const [aprovacao, setAprovacao] = useState(false);
  const [erroNome, setErroNome] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const campoNome = useRef<HTMLInputElement>(null);

  if (!pode(perfil.plano, 'criarLiga')) {
    return (
      <Tela>
        <Topo titulo="Criar comunidade" voltar={voltar} />
        <div className="cadeado-tentos">
          <p className="cadeado">Criar comunidade é do plano Ouro. Seu plano é o {NOME_DO_PLANO[perfil.plano]}: você entra nas comunidades pelo convite ou pela busca.</p>
          <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'mensal' })}>Ver planos</button>
        </div>
      </Tela>
    );
  }

  async function criar() {
    setErro(null);
    const n = nome.trim();
    if (!n) { setErroNome('Escreva o nome da comunidade.'); campoNome.current?.focus(); return; }
    if (textoOfensivo(n)) { setErroNome('Escolha outro nome para a comunidade.'); campoNome.current?.focus(); return; }
    if (textoOfensivo(descricao)) { setErro('Escolha outro texto para a descrição.'); return; }
    if (cidadePendente) { setErro('Escolha a cidade na lista ou apague o campo.'); return; }
    setCriando(true);
    try {
      const f = await criarComunidade(sessao, { nome: n, escudo, tipo, cidadeIbge: cidade, descricao: descricao.trim(), publica, aprovacao: publica && aprovacao });
      ir({ tela: 'comunidade', id: f.id }, true);
    } catch (e) {
      setErro((e as Error).message);
      setCriando(false);
    }
  }

  return (
    <Tela>
      <Topo titulo="Criar comunidade" voltar={voltar} />
      <div className="cartao criar-trio">
        <div className="criar-trio-topo">
          <Escudo id={escudo} tamanho={72} />
          <div className="campo">
            <label htmlFor="nome-comunidade">Nome da comunidade</label>
            <input id="nome-comunidade" ref={campoNome} value={nome} maxLength={NOME_DA_COMUNIDADE_MAX} autoComplete="off" placeholder="Truco da Firma" disabled={criando}
              aria-invalid={erroNome ? true : undefined} aria-describedby={erroNome ? 'erro-nome-comunidade' : undefined}
              onChange={(e) => { setNome(e.target.value); setErroNome(null); }} />
            {erroNome && <small id="erro-nome-comunidade" className="erro" role="alert">{erroNome}</small>}
          </div>
        </div>
        <EscolherEscudo valor={escudo} travado={criando} aoMudar={setEscudo} />
      </div>
      <section aria-labelledby="h-tipo" className="campo">
        <h2 id="h-tipo" className="rotulo">Tipo</h2>
        <Opcoes rotulo="Tipo da comunidade" classe="tipos-comunidade" valor={tipo} opcoes={TIPOS_DE_COMUNIDADE} travado={criando} aoMudar={setTipo} />
      </section>
      <CampoCidade codigo={cidade} erro={null} aoMudar={(c, pendente) => { setCidade(c); setCidadePendente(pendente); }} />
      <div className="campo">
        <label htmlFor="descricao-comunidade">Descrição <small>(opcional)</small></label>
        <textarea id="descricao-comunidade" value={descricao} maxLength={DESCRICAO_MAX} rows={3} disabled={criando} placeholder="Toda sexta depois do expediente."
          onChange={(e) => setDescricao(e.target.value)} />
        <small className="conta-letras">{[...descricao].length}/{DESCRICAO_MAX}</small>
      </div>
      <section aria-labelledby="h-quem-entra" className="campo">
        <h2 id="h-quem-entra" className="rotulo">Quem entra</h2>
        <Opcoes rotulo="Quem entra" valor={publica ? 'publica' : 'convite'} travado={criando} aoMudar={(v) => setPublica(v === 'publica')}
          opcoes={[['publica', 'Pública'], ['convite', 'Por convite']] as const} />
        <small>{publica ? 'Aparece na busca. Quem tem o link também entra.' : 'Entra só quem recebe o link de convite.'}</small>
        {publica && (
          <ul className="cartao regras-casa">
            <li><p><b>Pedir aprovação para entrar</b><span>Quem chega pela busca espera você ou um moderador aprovar. O link de convite entra direto.</span></p>
              <Chave rotulo="Pedir aprovação para entrar" ligada={aprovacao} travado={criando} aoMudar={setAprovacao} /></li>
          </ul>
        )}
      </section>
      <p className="nota">Você é o dono. O plano Ouro cria até {COMUNIDADES_POR_DONO} comunidades, com até {MEMBROS_MAX} membros cada.</p>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={criando} onClick={() => void criar()}>{criando ? 'CRIANDO...' : 'CRIAR COMUNIDADE'}</button>
    </Tela>
  );
}

// O cabeçalho: escudo, nome, tipo, cidade, membros e a descrição.
function Cabecalho({ f }: { f: FichaComunidade }) {
  return (
    <div className="cartao trio-cab comunidade-cab">
      <Escudo id={f.escudo} tamanho={64} />
      <div>
        <h2>{f.nome}</h2>
        <p className="nota">{linhaDaComunidade(f)}{f.publica ? '' : ' · Por convite'}</p>
        {f.descricao && <p className="descricao">{f.descricao}</p>}
      </div>
    </div>
  );
}

// Quem não é membro: a ficha com Entrar (ou Pedir para entrar).
function FichaDeFora({ sessao, f, convite, aoEntrar }: { sessao: Sessao; f: FichaComunidade; convite: string | null; aoEntrar: () => void }) {
  const [ocupado, setOcupado] = useState(false);
  const [pedido, setPedido] = useState(f.pedido);
  const [erro, setErro] = useState<string | null>(null);
  const comAprovacao = !convite && f.aprovacao;
  async function entrar() {
    setErro(null); setOcupado(true);
    try {
      const r = await entrarNaComunidade(sessao, f.id, convite);
      if (r.entrou) { medir('comunidade_entrou'); avisarNaTela(`Você entrou na ${f.nome}.`); aoEntrar(); } else setPedido(r.pedido);
    } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }
  return (
    <>
      <Cabecalho f={f} />
      <p className="nota">Dono: <b>{f.dono.apelido}</b></p>
      {pedido ? <p className="cartao nota">Pedido enviado. O dono ou um moderador aprova a sua entrada.</p>
          : (
            <>
              {erro && <p className="erro" role="alert">{erro}</p>}
              <button className="btn1 largo" disabled={ocupado} onClick={() => void entrar()}>{comAprovacao ? 'PEDIR PARA ENTRAR' : 'ENTRAR NA COMUNIDADE'}</button>
              {comAprovacao && <p className="nota centro">Esta comunidade aprova cada entrada.</p>}
            </>
          )}
    </>
  );
}

// O link de convite: /comunidade/CODIGO.
export function ConviteComunidadeTela({ sessao, codigo }: { sessao: Sessao; codigo: string }) {
  const [f, setF] = useState<FichaComunidade | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    comunidadePorConvite(sessao, codigo).then((x) => {
      if (!vivo) return;
      if (x.papel) ir({ tela: 'comunidade', id: x.id }, true); else setF(x);
    }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id, codigo]);
  if (erro) return <Tela><Topo titulo="Convite" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!f) return <Carregando />;
  return (
    <Tela>
      <Topo titulo="Convite para a comunidade" voltar={voltar} />
      <FichaDeFora sessao={sessao} f={f} convite={codigo} aoEntrar={() => ir({ tela: 'comunidade', id: f.id }, true)} />
    </Tela>
  );
}

const ABAS: [AbaDaComunidade, string][] = [['mural', 'Mural'], ['mesas', 'Mesas'], ['campeonatos', 'Campeonatos'], ['ranking', 'Ranking'], ['eventos', 'Eventos'], ['membros', 'Membros']];

export function ComunidadeTela({ sessao, perfil, id, aba = 'mural' }: { sessao: Sessao; perfil: PerfilConta; id: string; aba?: AbaDaComunidade }) {
  const [f, setF] = useState<FichaComunidade | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  useEffect(() => {
    let vivo = true;
    lerComunidade(sessao, id).then((x) => { if (vivo) setF(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id, id, versao]);
  if (erro) return <Tela><Topo titulo="Comunidade" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!f) return <Carregando />;
  if (!f.papel) return <Tela><Topo titulo="Comunidade" voltar={voltar} /><FichaDeFora sessao={sessao} f={f} convite={null} aoEntrar={() => setVersao((v) => v + 1)} /></Tela>;
  const gestor = f.papel !== 'membro';
  return (
    <Tela larga>
      <div className="comunidade">
        <Topo titulo="Comunidade" voltar={voltar} />
        <Cabecalho f={f} />
        {f.somenteLeitura && <p className="cartao aviso-leitura" role="status">Esta comunidade está só para leitura: as mesas seguem abertas. O dono precisa voltar ao Ouro ou passar a comunidade a um moderador com Ouro.</p>}
        {f.avisoSemOuro !== null && <p className="cartao aviso-leitura" role="status">O dono saiu do Ouro. Em {f.avisoSemOuro} dias, a comunidade fica só para leitura. Ele volta ao Ouro ou passa a comunidade a um moderador com Ouro.</p>}
        <div className="abas-comunidade" role="tablist" aria-label="Comunidade">
          {ABAS.map(([a, nome]) => (
            <button key={a} role="tab" aria-selected={aba === a} className={aba === a ? 'on' : undefined}
              onClick={() => { if (aba !== a) ir({ tela: 'comunidade', id, ...(a === 'mural' ? {} : { aba: a }) }, true); }}>{nome}</button>
          ))}
        </div>
        <div className="aba-comunidade" role="tabpanel">
          {aba === 'mural' && <Mural sessao={sessao} f={f} />}
          {aba === 'mesas' && <Mesas sessao={sessao} f={f} />}
          {aba === 'campeonatos' && <CampeonatosDaComunidade sessao={sessao} comunidade={f.id} />}
          {aba === 'ranking' && <RankingDaComunidade sessao={sessao} f={f} />}
          {aba === 'eventos' && <Eventos sessao={sessao} f={f} />}
          {aba === 'membros' && <Membros sessao={sessao} perfil={perfil} f={f} aoMudar={() => setVersao((v) => v + 1)} />}
        </div>
        <RodapeDaComunidade sessao={sessao} f={f} gestor={gestor} aoMudar={() => setVersao((v) => v + 1)} />
      </div>
    </Tela>
  );
}

// ---- mural ----
function Mural({ sessao, f }: { sessao: Sessao; f: FichaComunidade }) {
  const [posts, setPosts] = useState<PostNaTela[] | null>(null);
  const [podePublicar, setPodePublicar] = useState(false);
  const [maisAntigos, setMaisAntigos] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerMural(sessao, f.id).then((r) => { if (vivo) { setPosts(r.posts); setPodePublicar(r.podePublicar); setMaisAntigos(r.posts.length === 20); } }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [f.id]);
  async function carregarMais() {
    const ultimo = posts?.[posts.length - 1];
    if (!ultimo) return;
    const r = await lerMural(sessao, f.id, ultimo.criadoEm).catch(() => null);
    if (r) { setPosts((p) => [...(p ?? []), ...r.posts]); setMaisAntigos(r.posts.length === 20); }
  }
  return (
    <section aria-label="Mural">
      {podePublicar && <NovoPost sessao={sessao} f={f} aoPublicar={(p) => setPosts((ps) => [p, ...(ps ?? [])])} />}
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!posts && !erro && <p className="carregando" role="status">Carregando o mural...</p>}
      {posts && posts.length === 0 && <p className="cartao vazio-lista">{podePublicar ? 'O mural está vazio. Publique o primeiro recado da turma.' : 'O mural está vazio. O dono e os moderadores publicam aqui.'}</p>}
      <ul className="mural">
        {posts?.map((p) => <Post key={p.id} sessao={sessao} f={f} p={p} aoSair={() => setPosts((ps) => ps?.filter((x) => x.id !== p.id) ?? null)} />)}
      </ul>
      {maisAntigos && <button className="btn2 largo" onClick={() => void carregarMais()}>Ver posts mais antigos</button>}
    </section>
  );
}

function NovoPost({ sessao, f, aoPublicar }: { sessao: Sessao; f: FichaComunidade; aoPublicar: (p: PostNaTela) => void }) {
  const [texto, setTexto] = useState('');
  const [imagem, setImagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);
  async function escolher(a: File | undefined) {
    setErro(null);
    if (!a) return;
    try { setImagem(await lerImagem(a)); } catch (e) { setErro((e as Error).message); }
  }
  async function publicar() {
    setErro(null);
    if (!texto.trim()) { setErro('Escreva o recado.'); return; }
    if (textoOfensivo(texto)) { setErro('Escolha outro texto para o post.'); return; }
    setOcupado(true);
    try {
      aoPublicar(await publicarNoMural(sessao, f.id, texto.trim(), imagem));
      setTexto(''); setImagem(null);
      if (arquivo.current) arquivo.current.value = '';
    } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }
  return (
    <div className="cartao novo-post">
      <label className="campo">
        <span className="rotulo">Recado para a turma</span>
        <textarea value={texto} maxLength={POST_MAX} rows={3} disabled={ocupado} placeholder="Sexta tem mesa às 20h. Quem vem?" onChange={(e) => setTexto(e.target.value)} />
      </label>
      {imagem && (
        <div className="previa-imagem">
          <img src={imagem} alt="Imagem escolhida para o post" />
          <button className="link" onClick={() => { setImagem(null); if (arquivo.current) arquivo.current.value = ''; }}>Tirar imagem</button>
        </div>
      )}
      <div className="acoes-post">
        <label className="btn2 anexar">
          Imagem
          <input ref={arquivo} type="file" accept="image/jpeg,image/png,image/webp" className="escondido" disabled={ocupado} onChange={(e) => void escolher(e.target.files?.[0])} />
        </label>
        <small className="conta-letras">{[...texto].length}/{POST_MAX}</small>
        <button className="btn1" disabled={ocupado} onClick={() => void publicar()}>{ocupado ? 'PUBLICANDO...' : 'PUBLICAR'}</button>
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
    </div>
  );
}

function Post({ sessao, f, p, aoSair }: { sessao: Sessao; f: FichaComunidade; p: PostNaTela; aoSair: () => void }) {
  const [reacoes, setReacoes] = useState(p.reacoes);
  const [minha, setMinha] = useState(p.minha);
  const [menu, setMenu] = useState(false);
  async function reagir(emoji: string) {
    const nova = minha === emoji ? null : emoji;
    try { const r = await reagirAoPost(sessao, f.id, p.id, nova); setReacoes(r.reacoes); setMinha(r.minha); } catch (e) { avisarNaTela((e as Error).message); }
  }
  async function apagar() {
    setMenu(false);
    try { await apagarPost(sessao, f.id, p.id); aoSair(); avisarNaTela('Post apagado.'); } catch (e) { avisarNaTela((e as Error).message); }
  }
  async function denunciar() {
    setMenu(false);
    try { await denunciarPost(sessao, f.id, p.id); avisarNaTela('Denúncia enviada. Obrigado por avisar.'); } catch (e) { avisarNaTela((e as Error).message); }
  }
  return (
    <li className="cartao post">
      <div className="post-cab">
        <BotaoPerfil usuario={p.autor.usuario} apelido={p.autor.apelido}>
          <Rosto perfil={{ apelido: p.autor.apelido, avatar: p.autor.avatar }} usuario={p.autor.usuario} cor="var(--gold-600)" tamanho={40} />
        </BotaoPerfil>
        <span className="quem"><b>{p.autor.usuario === sessao.id ? 'Você' : p.autor.apelido}</b><small>{quandoFoi(Date.now(), p.criadoEm)}</small></span>
        <button className="mais-opcoes" aria-label="Opções do post" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>⋯</button>
        {menu && (
          <div className="menu-post" role="menu">
            {p.podeApagar && <button role="menuitem" onClick={() => void apagar()}>Apagar post</button>}
            {p.autor.usuario !== sessao.id && <button role="menuitem" onClick={() => void denunciar()}>Denunciar post</button>}
            <button role="menuitem" onClick={() => setMenu(false)}>Fechar</button>
          </div>
        )}
      </div>
      <p className="texto-post">{p.texto}</p>
      {p.imagem && <img className="imagem-post" src={p.imagem} alt={`Imagem do post de ${p.autor.apelido}`} loading="lazy" />}
      <div className="reacoes-post" role="group" aria-label="Reações">
        {REACOES_DO_MURAL.map((e) => (
          <button key={e} aria-pressed={minha === e} className={minha === e ? 'on' : undefined} aria-label={`Reagir com ${e}${reacoes[e] ? `, ${reacoes[e]}` : ''}`}
            onClick={() => void reagir(e)}>
            <span aria-hidden="true">{e}</span>{reacoes[e] ? <small aria-hidden="true">{reacoes[e]}</small> : null}
          </button>
        ))}
      </div>
    </li>
  );
}

// ---- mesas ----
function Mesas({ sessao, f }: { sessao: Sessao; f: FichaComunidade }) {
  const [salas, setSalas] = useState<SalaNaLista[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    const ler = () => lerMesasDaComunidade(sessao, f.id).then((s) => { if (vivo) { setSalas(s); setErro(null); } }, (e: Error) => { if (vivo) setErro(e.message); });
    void ler();
    const t = setInterval(() => void ler(), 15_000);
    return () => { vivo = false; clearInterval(t); };
  }, [f.id]);
  return (
    <section aria-label="Mesas da comunidade">
      <p className="nota">Mesas só dos membros. Contam como sala de amigo: ficam fora do limite diário da liga e entram no ranking da comunidade.</p>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!salas && !erro && <p className="carregando" role="status">Carregando as mesas...</p>}
      {salas && salas.length === 0 && <p className="cartao vazio-lista">Nenhuma mesa aberta agora. Abra uma e chame a turma.</p>}
      <ul className="mesas-comunidade">
        {salas?.map((s) => (
          <li key={s.codigo} className="cartao mesa-comunidade">
            <span className="quem"><b>{s.nome}</b>
              <small>{s.fase === 'espera' ? `${s.sentados} de 6 · na espera` : s.fase === 'jogo' ? 'Jogando agora' : 'Fim de partida'}{s.ingresso ? ` · ${s.ingresso} Tentos` : ''}</small></span>
            {s.fase === 'espera' && s.sentados < 6
              ? <button className="btn2 entrar" onClick={() => irParaMesa({ tela: 'sala', codigo: s.codigo })}>Entrar</button>
              : <button className="btn2" onClick={() => ir({ tela: 'assistir', codigo: s.codigo, amigo: null })}>Assistir</button>}
          </li>
        ))}
      </ul>
      <button className="btn1 largo" onClick={() => ir({ tela: 'nova-sala', ingresso: null, comunidade: f.id })}>ABRIR MESA DA COMUNIDADE</button>
    </section>
  );
}

// ---- ranking ----
const PERIODOS: [PeriodoDoRanking, string][] = [['semana', 'Semana'], ['mes', 'Mês'], ['temporada', 'Temporada']];
function RankingDaComunidade({ sessao, f }: { sessao: Sessao; f: FichaComunidade }) {
  const [periodo, setPeriodo] = useState<PeriodoDoRanking>('semana');
  const [linhas, setLinhas] = useState<{ periodo: PeriodoDoRanking; linhas: LinhaDoRanking[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerRankingDaComunidade(sessao, f.id, periodo).then((r) => { if (vivo) setLinhas(r); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [f.id, periodo]);
  const atual = linhas?.periodo === periodo ? linhas.linhas : null;
  return (
    <section aria-label="Ranking da comunidade">
      <div className="segmentos-periodo" role="radiogroup" aria-label="Período">
        {PERIODOS.map(([v, nome]) => <button key={v} role="radio" aria-checked={periodo === v} className={periodo === v ? 'on' : undefined} onClick={() => setPeriodo(v)}>{nome}</button>)}
      </div>
      <p className="nota">Só as partidas nas mesas da comunidade. Vitórias primeiro, depois o aproveitamento.</p>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!atual && !erro && <p className="carregando" role="status">Carregando o ranking...</p>}
      {atual && atual.length === 0 && <p className="cartao vazio-lista">Ninguém jogou nas mesas da comunidade {periodo === 'semana' ? 'nesta semana' : periodo === 'mes' ? 'neste mês' : 'nesta temporada'}.</p>}
      {atual && atual.length > 0 && (
        <ol className="lista-ranking ranking-comunidade">
          {atual.map((l) => (
            <li key={l.usuario} className={'linha-ranking' + (l.usuario === sessao.id ? ' meu' : '')}>
              <span className="posicao">{l.posicao}º</span>
              <BotaoPerfil usuario={l.usuario} apelido={l.apelido}>
                <Rosto perfil={{ apelido: l.apelido, avatar: l.avatar }} usuario={l.usuario} cor="var(--gold-600)" tamanho={36} />
              </BotaoPerfil>
              <span className="quem"><b>{l.usuario === sessao.id ? 'Você' : l.apelido}</b><small>{l.partidas} {l.partidas === 1 ? 'partida' : 'partidas'} · {l.aproveitamento}%</small></span>
              <span className="pontos">{l.vitorias}<small>{l.vitorias === 1 ? 'vitória' : 'vitórias'}</small></span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---- eventos ----
function Eventos({ sessao, f }: { sessao: Sessao; f: FichaComunidade }) {
  const [eventos, setEventos] = useState<EventoNaTela[] | null>(null);
  const [podeCriar, setPodeCriar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerEventos(sessao, f.id).then((r) => { if (vivo) { setEventos(r.eventos); setPodeCriar(r.podeCriar); } }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [f.id]);
  async function cancelar(e: EventoNaTela) {
    try { await cancelarEvento(sessao, f.id, e.id); setEventos((l) => l?.filter((x) => x.id !== e.id) ?? null); avisarNaTela('Evento cancelado.'); } catch (x) { avisarNaTela((x as Error).message); }
  }
  return (
    <section aria-label="Eventos">
      <p className="nota">Os membros recebem um aviso 1 dia antes e 1 hora antes.</p>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!eventos && !erro && <p className="carregando" role="status">Carregando os eventos...</p>}
      {eventos && eventos.length === 0 && <p className="cartao vazio-lista">Nenhum evento marcado.</p>}
      <ul className="eventos-comunidade">
        {eventos?.map((e) => (
          <li key={e.id} className="cartao evento">
            <span className="quem"><b>{e.titulo}</b><small>{quandoDoEvento(e.quando)}{e.sala ? ` · Mesa ${e.sala}` : ''}</small></span>
            <span className="acoes-evento">
              {e.sala && <button className="btn2 entrar" onClick={() => irParaMesa({ tela: 'sala', codigo: e.sala! })}>Entrar</button>}
              {podeCriar && <button className="btn2" aria-label={`Cancelar o evento ${e.titulo}`} onClick={() => void cancelar(e)}>Cancelar</button>}
            </span>
          </li>
        ))}
      </ul>
      {podeCriar && <NovoEvento sessao={sessao} f={f} aoCriar={(e) => setEventos((l) => [...(l ?? []), e].sort((a, b) => a.quando - b.quando))} />}
    </section>
  );
}

function hojeEmBrasilia(): string {
  return new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
}

function NovoEvento({ sessao, f, aoCriar }: { sessao: Sessao; f: FichaComunidade; aoCriar: (e: EventoNaTela) => void }) {
  const [titulo, setTitulo] = useState('');
  const [data, setData] = useState(hojeEmBrasilia);
  const [hora, setHora] = useState('20:00');
  const [sala, setSala] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function criar() {
    setErro(null);
    const quando = instanteDe(data, hora);
    if (!titulo.trim()) { setErro('Escreva o título do evento.'); return; }
    if (textoOfensivo(titulo)) { setErro('Escolha outro texto para o título.'); return; }
    if (!quando || quando < Date.now()) { setErro('Escolha uma data e hora daqui para a frente.'); return; }
    const codigo = sala.trim().toUpperCase();
    if (codigo && !/^[A-Z2-9]{6}$/.test(codigo)) { setErro('O código da mesa tem 6 letras e números.'); return; }
    setOcupado(true);
    try {
      aoCriar(await criarEvento(sessao, f.id, { titulo: titulo.trim(), quando, sala: codigo || null }));
      setTitulo(''); setSala('');
      avisarNaTela('Evento marcado. Os membros recebem o aviso.');
    } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }
  return (
    <div className="cartao novo-evento">
      <h3 className="rotulo">Marcar evento</h3>
      <label className="campo"><span>Título</span>
        <input value={titulo} maxLength={TITULO_EVENTO_MAX} placeholder="Torneio de sexta" disabled={ocupado} onChange={(e) => setTitulo(e.target.value)} /></label>
      <div className="linha-campos">
        <label className="campo"><span>Data</span><input type="date" value={data} min={hojeEmBrasilia()} disabled={ocupado} onChange={(e) => setData(e.target.value)} /></label>
        <label className="campo"><span>Hora</span><input type="time" value={hora} disabled={ocupado} onChange={(e) => setHora(e.target.value)} /></label>
      </div>
      <label className="campo"><span>Código da mesa <small>(opcional)</small></span>
        <input value={sala} maxLength={6} placeholder="K7QX2M" autoCapitalize="characters" disabled={ocupado} onChange={(e) => setSala(e.target.value)} /></label>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void criar()}>{ocupado ? 'MARCANDO...' : 'MARCAR EVENTO'}</button>
    </div>
  );
}

// ---- membros ----
function Membros({ sessao, perfil, f, aoMudar }: { sessao: Sessao; perfil: PerfilConta; f: FichaComunidade; aoMudar: () => void }) {
  const [dados, setDados] = useState<{ membros: MembroNaLista[]; pedidos: PedidoDeEntrada[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const [aberto, setAberto] = useState<MembroNaLista | null>(null);
  const [chamar, setChamar] = useState(false);
  useEffect(() => {
    let vivo = true;
    lerMembros(sessao, f.id).then((r) => { if (vivo) setDados(r); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [f.id, versao]);
  const gestor = f.papel !== 'membro';
  async function decidir(p: PedidoDeEntrada, aprovar: boolean) {
    try { await decidirPedido(sessao, f.id, p.usuario, aprovar); avisarNaTela(aprovar ? `${p.apelido} entrou na comunidade.` : 'Pedido recusado.'); setVersao((v) => v + 1); } catch (e) { avisarNaTela((e as Error).message); }
  }
  const pode = (m: MembroNaLista) => m.usuario !== sessao.id && m.papel !== 'dono' && (f.papel === 'dono' || (f.papel === 'moderador' && m.papel === 'membro'));
  return (
    <section aria-label="Membros">
      {gestor && f.convite && <ConviteDaComunidade sessao={sessao} f={f} aoMudar={aoMudar} />}
      <button className="btn2 largo chamar-amigos" onClick={() => setChamar(true)}>Chamar amigos para a comunidade</button>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {gestor && dados && dados.pedidos.length > 0 && (
        <>
          <h2 className="rotulo">Pedidos para entrar · {dados.pedidos.length}</h2>
          <ul className="membros-comunidade">
            {dados.pedidos.map((p) => (
              <li key={p.usuario} className="cartao">
                <Rosto perfil={{ apelido: p.apelido, avatar: p.avatar }} usuario={p.usuario} cor="var(--gold-600)" tamanho={40} />
                <span className="quem"><b>{p.apelido}</b><small>Pediu {quandoFoi(Date.now(), p.em)}</small></span>
                <span className="resposta-trio">
                  <button className="btn2 entrar" aria-label={`Aprovar ${p.apelido}`} onClick={() => void decidir(p, true)}>Aprovar</button>
                  <button className="btn2 recusar-trio" aria-label={`Recusar ${p.apelido}`} onClick={() => void decidir(p, false)}>Recusar</button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {!dados && !erro && <p className="carregando" role="status">Carregando os membros...</p>}
      {dados && (
        <>
          <h2 className="rotulo">Membros · {dados.membros.length} de {MEMBROS_MAX}</h2>
          <ul className="membros-comunidade">
            {dados.membros.map((m) => (
              <li key={m.usuario} className="cartao">
                <BotaoPerfil usuario={m.usuario} apelido={m.apelido}>
                  <Rosto perfil={{ apelido: m.apelido, avatar: m.avatar }} usuario={m.usuario} cor="var(--gold-600)" tamanho={40} />
                </BotaoPerfil>
                <span className="quem"><b>{m.usuario === sessao.id ? 'Você' : m.apelido}</b>
                  {m.papel !== 'membro' && <small className="papel">{PAPEL[m.papel]}</small>}
                  <small>{m.divisao ? rotuloDaDivisao(m.divisao) : 'Café com Leite III'}</small></span>
                {m.usuario !== sessao.id && <BotaoAmizade usuario={m.usuario} apelido={m.apelido} compacto />}
                {pode(m) && <button className="mais-opcoes" aria-label={`Opções de ${m.apelido}`} onClick={() => setAberto(m)}>⋯</button>}
              </li>
            ))}
          </ul>
        </>
      )}
      {aberto && <OpcoesDoMembro sessao={sessao} f={f} m={aberto} aoFechar={(mudou) => { setAberto(null); if (mudou) { setVersao((v) => v + 1); aoMudar(); } }} />}
      {chamar && <ChamarAmigos sessao={sessao} perfil={perfil} f={f} membros={new Set(dados?.membros.map((m) => m.usuario))} aoFechar={() => setChamar(false)} />}
    </section>
  );
}

function ConviteDaComunidade({ sessao, f, aoMudar }: { sessao: Sessao; f: FichaComunidade; aoMudar: () => void }) {
  const [convite, setConvite] = useState(f.convite!);
  const [copiado, setCopiado] = useState<string | null>(null);
  const link = linkDaComunidade(convite);
  async function copiar() {
    try { await navigator.clipboard.writeText(link); setCopiado('Link copiado!'); } catch { setCopiado('Copie o link: ' + link); }
    medir('share', { method: 'link', content_type: 'comunidade' });
    setTimeout(() => setCopiado(null), 2500);
  }
  async function trocar() {
    try { const r = await novoConvite(sessao, f.id); setConvite(r.convite); avisarNaTela('Link novo pronto. O antigo parou de valer.'); aoMudar(); } catch (e) { avisarNaTela((e as Error).message); }
  }
  return (
    <div className="cartao convite-comunidade">
      <h2 className="rotulo">Link de convite</h2>
      <p className="link-convite">{link.replace(/^https?:\/\//, '')}</p>
      <div className="acoes-convite">
        <button className="btn2" onClick={() => void copiar()}>Copiar link</button>
        <a className="btn2 whats" href={'https://wa.me/?text=' + encodeURIComponent(`Entra na comunidade ${f.nome} no Truco de 6: ${link}`)} target="_blank" rel="noreferrer"
          onClick={() => medir('share', { method: 'whatsapp', content_type: 'comunidade' })}>WhatsApp</a>
        <button className="btn2" onClick={() => void trocar()}>Gerar link novo</button>
      </div>
      {copiado && <p className="nota" role="status">{copiado}</p>}
      <small className="nota">Quem foi removido só volta por um link gerado depois da remoção.</small>
    </div>
  );
}

export function Folha({ titulo, aoFechar, children }: { titulo: string; aoFechar: () => void; children: React.ReactNode }) {
  return createPortal(
    <div className="dialogo-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <div className="dialogo folha-comunidade" role="dialog" aria-modal="true" aria-labelledby="titulo-folha">
        <h2 id="titulo-folha">{titulo}</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function OpcoesDoMembro({ sessao, f, m, aoFechar }: { sessao: Sessao; f: FichaComunidade; m: MembroNaLista; aoFechar: (mudou: boolean) => void }) {
  const [confirmar, setConfirmar] = useState<'remover' | 'transferir' | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function fazer(acao: () => Promise<unknown>, texto: string) {
    setOcupado(true);
    try { await acao(); avisarNaTela(texto); aoFechar(true); } catch (e) { avisarNaTela((e as Error).message); setOcupado(false); }
  }
  if (confirmar === 'remover') {
    return (
      <Folha titulo={`Remover ${m.apelido}?`} aoFechar={() => aoFechar(false)}>
        <p>{m.apelido} sai da comunidade e só volta por um link de convite novo.</p>
        <button className="btn1 largo" disabled={ocupado} onClick={() => void fazer(() => removerMembro(sessao, f.id, m.usuario), `${m.apelido} saiu da comunidade.`)}>REMOVER</button>
        <button className="btn2 largo" onClick={() => aoFechar(false)}>Agora não</button>
      </Folha>
    );
  }
  if (confirmar === 'transferir') {
    return (
      <Folha titulo={`Passar a comunidade para ${m.apelido}?`} aoFechar={() => aoFechar(false)}>
        <p>{m.apelido} vira o dono e você vira moderador. O novo dono precisa ter o plano Ouro.</p>
        <button className="btn1 largo" disabled={ocupado} onClick={() => void fazer(() => transferirComunidade(sessao, f.id, m.usuario), `${m.apelido} agora é o dono.`)}>PASSAR A COMUNIDADE</button>
        <button className="btn2 largo" onClick={() => aoFechar(false)}>Agora não</button>
      </Folha>
    );
  }
  return (
    <Folha titulo={m.apelido} aoFechar={() => aoFechar(false)}>
      {f.papel === 'dono' && m.papel === 'membro' && (
        <button className="btn2 largo" disabled={ocupado} onClick={() => void fazer(() => definirPapel(sessao, f.id, m.usuario, 'moderador'), `${m.apelido} agora é moderador.`)}>Tornar moderador</button>
      )}
      {f.papel === 'dono' && m.papel === 'moderador' && (
        <>
          <button className="btn2 largo" disabled={ocupado} onClick={() => void fazer(() => definirPapel(sessao, f.id, m.usuario, 'membro'), `${m.apelido} voltou a membro.`)}>Tirar de moderador</button>
          <button className="btn2 largo" disabled={ocupado} onClick={() => setConfirmar('transferir')}>Passar a comunidade</button>
        </>
      )}
      <button className="btn2 largo" disabled={ocupado} onClick={() => setConfirmar('remover')}>Remover da comunidade</button>
      <button className="btn2 largo" onClick={() => aoFechar(false)}>Fechar</button>
    </Folha>
  );
}

function ChamarAmigos({ sessao, f, membros, aoFechar }: { sessao: Sessao; perfil: PerfilConta; f: FichaComunidade; membros: Set<string>; aoFechar: () => void }) {
  const [amigos, setAmigos] = useState<AmigoNaLista[] | null>(null);
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { lerAmigos(sessao).then((r) => setAmigos(r.amigos.filter((a) => !membros.has(a.usuario))), () => setAmigos([])); }, []);
  async function mandar() {
    setOcupado(true);
    try {
      const r = await convidarAmigos(sessao, f.id, [...escolhidos]);
      avisarNaTela(r.enviados ? `${r.enviados} ${r.enviados === 1 ? 'convite enviado' : 'convites enviados'}.` : 'Esses amigos já tinham convite.');
      aoFechar();
    } catch (e) { avisarNaTela((e as Error).message); setOcupado(false); }
  }
  return (
    <Folha titulo="Chamar amigos" aoFechar={aoFechar}>
      {!amigos && <p className="carregando">Carregando seus amigos...</p>}
      {amigos && amigos.length === 0 && <p className="nota">Todos os seus amigos já estão aqui, ou você ainda não tem amigos no jogo.</p>}
      {amigos && amigos.length > 0 && (
        <ul className="escolher-amigos">
          {amigos.map((a) => {
            const on = escolhidos.has(a.usuario);
            return (
              <li key={a.usuario}>
                <button role="checkbox" aria-checked={on} className={'cartao' + (on ? ' on' : '')}
                  onClick={() => setEscolhidos((s) => { const n = new Set(s); if (on) n.delete(a.usuario); else n.add(a.usuario); return n; })}>
                  <Rosto perfil={{ apelido: a.apelido, avatar: a.avatar }} usuario={a.usuario} cor="var(--gold-400)" tamanho={36} />
                  <b>{a.apelido}</b>
                  <span className="marca-escolha" aria-hidden="true">{on ? '✓' : ''}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button className="btn1 largo" disabled={ocupado || escolhidos.size === 0} onClick={() => void mandar()}>
        {escolhidos.size ? `CHAMAR ${escolhidos.size}` : 'ESCOLHA OS AMIGOS'}
      </button>
      <button className="btn2 largo" onClick={aoFechar}>Fechar</button>
    </Folha>
  );
}

// ---- rodapé: editar (dono), sair e denunciar ----
function RodapeDaComunidade({ sessao, f, gestor, aoMudar }: { sessao: Sessao; f: FichaComunidade; gestor: boolean; aoMudar: () => void }) {
  const [editar, setEditar] = useState(false);
  const [sair, setSair] = useState(false);
  async function denunciar() {
    try { await denunciarComunidade(sessao, f.id); avisarNaTela('Denúncia enviada. Obrigado por avisar.'); } catch (e) { avisarNaTela((e as Error).message); }
  }
  return (
    <div className="rodape-comunidade">
      {f.papel === 'dono' && <button className="btn2" onClick={() => setEditar(true)}>Editar comunidade</button>}
      {f.papel !== 'dono' && <button className="btn2" onClick={() => setSair(true)}>Sair da comunidade</button>}
      {!gestor && <button className="link" onClick={() => void denunciar()}>Denunciar comunidade</button>}
      {editar && <EditarComunidade sessao={sessao} f={f} aoFechar={(mudou) => { setEditar(false); if (mudou) aoMudar(); }} />}
      {sair && (
        <Folha titulo={`Sair da ${f.nome}?`} aoFechar={() => setSair(false)}>
          <p>Você deixa de ver o mural, as mesas e os eventos. Para voltar, entre de novo pela busca ou por um convite.</p>
          <button className="btn1 largo" onClick={() => { void sairDaComunidade(sessao, f.id).then(() => { avisarNaTela('Você saiu da comunidade.'); ir({ tela: 'ranking', aba: 'comunidades' }, true); }, (e: Error) => avisarNaTela(e.message)); }}>SAIR</button>
          <button className="btn2 largo" onClick={() => setSair(false)}>Agora não</button>
        </Folha>
      )}
    </div>
  );
}

function EditarComunidade({ sessao, f, aoFechar }: { sessao: Sessao; f: FichaComunidade; aoFechar: (mudou: boolean) => void }) {
  const [nome, setNome] = useState(f.nome);
  const [descricao, setDescricao] = useState(f.descricao);
  const [escudo, setEscudo] = useState(f.escudo);
  const [publica, setPublica] = useState(f.publica);
  const [aprovacao, setAprovacao] = useState(f.aprovacao);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function salvar() {
    setErro(null);
    if (!nome.trim()) { setErro('Escreva o nome da comunidade.'); return; }
    if (textoOfensivo(nome) || textoOfensivo(descricao)) { setErro('Escolha outro texto.'); return; }
    setOcupado(true);
    try {
      await editarComunidade(sessao, f.id, { nome: nome.trim(), descricao: descricao.trim(), escudo, publica, aprovacao: publica && aprovacao });
      avisarNaTela('Comunidade atualizada.');
      aoFechar(true);
    } catch (e) {
      setErro(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
      setOcupado(false);
    }
  }
  return (
    <Folha titulo="Editar comunidade" aoFechar={() => aoFechar(false)}>
      <label className="campo"><span>Nome</span>
        <input value={nome} maxLength={NOME_DA_COMUNIDADE_MAX} disabled={ocupado} onChange={(e) => setNome(e.target.value)} /></label>
      <EscolherEscudo valor={escudo} travado={ocupado} aoMudar={setEscudo} />
      <label className="campo"><span>Descrição</span>
        <textarea value={descricao} maxLength={DESCRICAO_MAX} rows={3} disabled={ocupado} onChange={(e) => setDescricao(e.target.value)} /></label>
      <Opcoes rotulo="Quem entra" valor={publica ? 'publica' : 'convite'} travado={ocupado} aoMudar={(v) => setPublica(v === 'publica')}
        opcoes={[['publica', 'Pública'], ['convite', 'Por convite']] as const} />
      {publica && (
        <ul className="cartao regras-casa">
          <li><p><b>Pedir aprovação para entrar</b></p><Chave rotulo="Pedir aprovação para entrar" ligada={aprovacao} travado={ocupado} aoMudar={setAprovacao} /></li>
        </ul>
      )}
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void salvar()}>SALVAR</button>
      <button className="btn2 largo" onClick={() => aoFechar(false)}>Cancelar</button>
    </Folha>
  );
}

// Nova mesa da comunidade: o nome vem da ficha, o resto é a tela de sempre.
export function NovaMesaDaComunidade({ sessao, perfil, id }: { sessao: Sessao; perfil: PerfilConta; id: string }) {
  const [f, setF] = useState<FichaComunidade | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => { lerComunidade(sessao, id).then(setF, (e: Error) => setErro(e.message)); }, [id]);
  if (erro) return <Tela><Topo titulo="Mesa da comunidade" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!f) return <Carregando />;
  if (!f.papel) return <Tela><Topo titulo="Mesa da comunidade" voltar={voltar} /><p className="erro" role="alert">Só os membros abrem mesa na comunidade.</p></Tela>;
  return <CriarSala sessao={sessao} perfil={perfil} comunidade={{ id: f.id, nome: f.nome }} />;
}
